import { Link } from 'react-router-dom'
import { useVehiclesQuery } from '../../hooks/useVehicleQueries'
import type { Vehicle } from '../../data/vehicles'
import { nameOf } from '../../data/vehicles'
import { assetUrl } from '../../lib/http'
import { useGsapContext } from '../../hooks/useGsapReveal'
import { useSection } from '../../cms/cms-context'
import { revealOnScroll } from '../../animations/gsapAnimations'

/* The track slides by exactly half its width, so the second half lands where
   the first started — a seamless loop. That only reads as one while each half
   already overruns the viewport, so the number of copies is dealt from the row
   itself: a floor holding two cars repeats them far more often than one
   holding twenty. Measured in the tile's own stride — width plus gap at sm and
   up, the widest the tiles ever get. */
const TILE_STRIDE = 265 + 24

/* Wide enough to cover an ultrawide desktop with the edge fade to spare. */
const HALF_TRACK_MIN = 2400

/* Pixels a second. The pace lives here rather than in a fixed duration: the
   same seconds spent on a track that grows with the catalogue would run a
   full floor past as a blur, and on a two-car one it would barely crawl. */
const ROW_SPEED = 58

/* How often the row repeats across the whole track, and how long one lap
   takes at that pace. */
function trackOf(length: number) {
  const perHalf = Math.max(2, Math.ceil(HALF_TRACK_MIN / (length * TILE_STRIDE)))
  return {
    copies: Array.from({ length: perHalf * 2 }, (_, copy) => copy),
    duration: (perHalf * length * TILE_STRIDE) / ROW_SPEED,
  }
}

const GHOST = 'Sultan Motors · The Car Lounge · '

/* The lettering is its own measure — one copy already overruns any viewport,
   so four of them stay plenty. */
const GHOST_COPIES = [0, 1, 2, 3]

/* One photo tile in the stream: the cut-out alone, uncaptioned — the band is
   a passing glance, and the name waits on the car's own page. It still
   reaches a screen reader through the alt text. Duplicate copies are
   decoration, so their links leave the tab order and the accessibility tree —
   a keyboard user meets each car once. */
function VehicleTile({ vehicle, decorative }: { vehicle: Vehicle; decorative: boolean }) {
  return (
    <Link
      to={`/cars/${vehicle.id}`}
      tabIndex={decorative ? -1 : undefined}
      aria-hidden={decorative || undefined}
      className="group/tile relative block h-[150px] w-[235px] overflow-hidden rounded-2xl sm:h-[168px] sm:w-[265px]"
    >
      {/* No frame around the car: the cut-outs stand straight on the band, lit
          from below by the same lime pool the rest of the floor uses. */}
      <span
        aria-hidden="true"
        className="absolute inset-x-[6%] bottom-0 h-[34%] rounded-[50%] bg-[radial-gradient(closest-side,rgb(200_245_66/0.16),transparent)]"
      />
      <img
        src={assetUrl(vehicle.images[0])}
        alt={decorative ? '' : nameOf(vehicle)}
        loading="lazy"
        decoding="async"
        width={530}
        height={336}
        className="relative size-full object-contain [filter:drop-shadow(0_4px_4px_rgb(0_0_0/0.7))] transition duration-700 ease-lux group-hover/tile:scale-[1.06]"
      />
    </Link>
  )
}

/* Placeholder tiles keep the band's height while the catalogue loads, so the
   page does not jump when the photographs land. */
function TileSkeletonRow() {
  return (
    <ul className="flex w-max items-center gap-5 sm:gap-6" aria-hidden="true">
      {Array.from({ length: 8 }).map((_, index) => (
        <li
          key={index}
          className="h-[150px] w-[235px] shrink-0 animate-pulse rounded-2xl bg-white/[0.04] sm:h-[168px] sm:w-[265px]"
        />
      ))}
    </ul>
  )
}

/* Two tickers, one band. Behind: the house marque as ghost lettering,
   oversized and running slow in reverse — embossing, not copy. In front: the
   stock itself, straight from the catalogue, as a single row of photographs
   passing. Hovering anywhere on the band pauses the traffic so a tile can
   actually be read and clicked; every tile opens its car's detail page. The
   loop stays on CSS — an infinite transform and a GSAP transform on one
   element would fight. */
function Brands() {
  const cms = useSection()

  /* One space either side, so the lettering reads as a continuous line
     however short the words somebody types are. */
  const ghost = `${cms.text('ghost', GHOST).trim()} · `

  const { data: vehicles = [], isPending } = useVehiclesQuery({ unsold: true })

  /* One tile per car standing on the floor, in catalogue order. Only cars
     that have been photographed belong in a photo stream — a tile with a torn
     image icon sells nothing — and the photograph is whichever one the API
     resolved for that car: its own shoot where it has been shot, otherwise
     the gallery its trim and model year was shot in. Nothing is drawn from a
     trim that has no car standing in it. */
  const row = vehicles.filter((vehicle) => vehicle.images[0])

  /* The label fades up, then the row rolls in from the edge it is already
     travelling from, before the marquee takes over. The slide is on a frame
     around the row, never on the track itself: the track's transform belongs
     to the CSS loop. Keyed on the car count because the row arrives with the
     catalogue. */
  const ref = useGsapContext<HTMLElement>(
    (scope) => {
      const label = scope.querySelector('[data-label]')
      if (label) revealOnScroll(label, { y: 16, start: 'top 92%' })

      const track = scope.querySelector('[data-row]')
      if (track) revealOnScroll(track, { x: 96, y: 0, duration: 1.2, start: 'top 94%' })
    },
    [row.length],
  )

  const { copies, duration } = trackOf(Math.max(1, row.length))

  return (
    <section
      ref={ref}
      aria-labelledby="brands-title"
      className="group relative overflow-hidden border-y border-line-soft band-surface py-14"
    >
      {/* ---------- ghost ticker ---------- */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 overflow-hidden select-none"
      >
        <p className="flex w-max animate-marquee-slow whitespace-nowrap font-display text-[clamp(5rem,11vw,9.5rem)] leading-none font-bold tracking-[-0.01em] text-ghost group-hover:[animation-play-state:paused]">
          {GHOST_COPIES.map((copy) => (
            <span key={copy} className="shrink-0">
              {ghost}
            </span>
          ))}
        </p>
      </div>

      <div className="relative mx-auto max-w-[1320px] px-5 sm:px-8">
        <h2
          id="brands-title"
          data-label
          {...cms.field('title')}
          className="flex items-center justify-center gap-4 text-center label text-lime"
        >
          <span aria-hidden="true" className="h-px w-10 coachline" />
          {cms.text('title', 'Fresh from the showroom floor')}
          <span aria-hidden="true" className="h-px w-10 coachline" />
        </h2>
      </div>

      {/* ---------- stock ticker ----------
          Only while there is something to run: a floor nobody has photographed
          yet leaves the band as the label over its ghost lettering rather than
          an empty gap. */}
      {isPending || row.length > 0 ? (
        <div className="relative mt-10 overflow-hidden edge-fade">
          {isPending ? (
            <TileSkeletonRow />
          ) : (
            <div data-row>
              <ul
                style={{ animationDuration: `${duration.toFixed(1)}s` }}
                className="flex w-max animate-marquee items-center gap-5 group-hover:[animation-play-state:paused] sm:gap-6"
              >
                {copies.map((copy) =>
                  row.map((vehicle) => (
                    <li key={`${copy}-${vehicle.id}`} className="shrink-0">
                      <VehicleTile vehicle={vehicle} decorative={copy > 0} />
                    </li>
                  )),
                )}
              </ul>
            </div>
          )}
        </div>
      ) : null}
    </section>
  )
}

export default Brands
